import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { TothChat } from "@/components/toth/TothChat";
import { BriefingPanel } from "@/components/toth/BriefingPanel";
import { ProposalsPanel } from "@/components/toth/ProposalsPanel";
import { InstructPanel } from "@/components/toth/InstructPanel";
import { TakeoversPanel } from "@/components/toth/TakeoversPanel";
import { ShieldCheck, ShieldOff, Play, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/common";
import { fmtDT } from "@/lib/format";
import { cn } from "@/lib/utils";

const LEVEL = { 1: "Osservazione", 2: "Autonomia autorizzata", 3: "Richiede approvazione", 4: "Non delegabile" };
const ACTION_TONE = { eseguita: "bg-emerald-100 text-emerald-800", negata: "bg-red-100 text-red-700", proposta: "bg-amber-100 text-amber-800", rifiutata: "bg-zinc-200 text-zinc-700", approvata_da_eseguire: "bg-sky-100 text-sky-800" };
const POLICY_LABEL = { bassa: "Criticità bassa", media: "Ordini normali / criticità media", alta: "Ordini alta / criticità alta", urgente: "Criticità emergenza" };

const Overview = ({ s, reload }) => {
  const [budget, setBudget] = useState(s.ai_budget_monthly_usd);
  const save = async (patch) => {
    try {
      await api.put("/toth/settings", { toth_enabled: s.toth_enabled, ai_budget_monthly_usd: Number(budget), policies: s.policies, ...patch });
      toast.success("Impostazioni salvate");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const run = async () => {
    try {
      const { data } = await api.post("/toth/run");
      toast.success(`Controllo eseguito: ${data.reminders} solleciti, ${data.escalations} escalation, ${data.new_overdue} nuovi scaduti`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const u = s.usage;
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <div className={cn("rounded-2xl p-5 text-white", s.toth_enabled ? "brand-gradient" : "bg-red-600")} data-testid="toth-killswitch-card">
        {s.toth_enabled ? <ShieldCheck className="h-7 w-7" /> : <ShieldOff className="h-7 w-7" />}
        <p className="mt-3 text-xl font-extrabold">{s.toth_enabled ? "Azioni automatiche attive" : "Azioni automatiche disattivate"}</p>
        <p className="mt-1 text-sm text-white/85">Interruttore di emergenza: disattivando Toth si fermano chat AI, briefing, proposte e solleciti automatici. Il resto di DR APP continua a funzionare e il motore delle scadenze continua a segnare i ritardi.</p>
        <label className="mt-4 flex items-center justify-between rounded-xl bg-white/20 px-4 py-3 font-bold">Toth abilitato
          <Switch checked={s.toth_enabled} onCheckedChange={(v) => save({ toth_enabled: v })} data-testid="toth-kill-switch-toggle" /></label>
      </div>
      <div className="card-soft p-5" data-testid="toth-usage-card">
        <p className="text-sm font-semibold text-muted-foreground">Consumo AI del mese</p>
        <p className="mt-2 text-3xl font-extrabold">$ {u.cost_usd.toFixed(2)} <span className="text-base font-semibold text-muted-foreground">/ {u.budget_usd}</span></p>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-muted"><div className="h-full bg-primary" style={{ width: `${Math.min(100, (u.cost_usd / (u.budget_usd || 1)) * 100)}%` }} /></div>
        <p className="mt-2 text-xs text-muted-foreground" data-testid="toth-usage-calls">{u.calls} chiamate · {u.model} · costo stimato su listino{u.budget_exceeded && <b className="text-red-600"> · budget esaurito: AI bloccata</b>}</p>
        {u.by_user?.length > 0 && <div className="mt-2 max-h-28 overflow-y-auto text-xs" data-testid="toth-usage-by-user">{u.by_user.map((r) => <p key={r.user} className="flex justify-between border-b py-1"><span>{r.user}</span><b>$ {r.cost_usd.toFixed(3)} · {r.calls}</b></p>)}</div>}
        <div className="mt-4 flex gap-2"><Input type="number" min="0" value={budget} onChange={(e) => setBudget(e.target.value)} className="rounded-xl" data-testid="toth-budget-input" />
          <Button onClick={() => save({})} className="rounded-xl" data-testid="toth-budget-save">Salva budget</Button></div>
      </div>
      <div className="card-soft p-5">
        <p className="text-sm font-semibold text-muted-foreground">Motore scadenze (deterministico)</p>
        <p className="mt-2 text-sm">Eseguito automaticamente lato server ogni 15 minuti, anche con utenti disconnessi.</p>
        <Button onClick={run} className="mt-4 h-11 w-full rounded-xl font-bold" data-testid="toth-run-engine-button"><Play className="mr-1 h-4 w-4" />Esegui controllo ora</Button>
      </div>
    </div>
  );
};

const Rules = ({ isCeo }) => {
  const [rules, setRules] = useState([]);
  const [runs, setRuns] = useState({});
  const load = useCallback(() => {
    api.get("/toth/rules").then((r) => setRules(r.data));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const toggle = async (r, on) => {
    try {
      await api.put(`/toth/rules/${r.id}/status`, { status: on ? "attiva" : "disattivata" });
      toast.success(on ? "Regola attivata" : "Regola disattivata");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const showRuns = async (r) => {
    const { data } = await api.get(`/toth/rules/${r.id}/runs`);
    setRuns({ ...runs, [r.id]: data });
  };
  const remove = async (r) => {
    try {
      await api.delete(`/toth/rules/${r.id}`);
      toast.success("Bozza eliminata");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-3">
      {isCeo && <InstructPanel onCreated={load} />}
      {rules.map((r) => (
        <div key={r.id} className={`card-soft p-5 ${r.status === "bozza" ? "ring-2 ring-amber-300" : ""}`} data-testid={`toth-rule-${r.kind}`}>
          <div className="flex items-start gap-4">
            <div className="min-w-0 flex-1">
              <p className="text-lg font-bold">{r.name}{r.status === "bozza" && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-800" data-testid={`toth-rule-draft-${r.id}`}>Bozza da approvare</span>}</p>
              <p className="text-sm text-muted-foreground">{r.kind_label}</p>
              {r.explanation && <p className="mt-1 text-sm">{r.explanation}</p>}
              {r.source_text && r.created_via === "istruisci_toth" && <p className="mt-1 text-xs italic text-muted-foreground">Istruzione: «{r.source_text}»</p>}
              <p className="mt-2 text-xs text-muted-foreground">Ambito: {r.scope}{r.params?.priorities && ` · priorità ${r.params.priorities.join(", ")}`}{r.params?.hours_before && ` · ${r.params.hours_before} ore prima`} · Approvata il {fmtDT(r.approved_at)} · Ultima esecuzione {fmtDT(r.last_run_at)} · {r.runs} esecuzioni · {r.actions} azioni prodotte</p>
            </div>
            <Switch checked={r.status === "attiva"} disabled={!isCeo} onCheckedChange={(v) => toggle(r, v)} data-testid={`toth-rule-toggle-${r.status === "bozza" ? r.id : r.kind}`} />
          </div>
          {isCeo && r.status === "bozza" && <button className="mr-4 mt-3 text-sm font-semibold text-red-600" onClick={() => remove(r)} data-testid={`toth-rule-delete-${r.id}`}>Elimina bozza</button>}
          <button className="mt-3 text-sm font-semibold text-primary" onClick={() => showRuns(r)} data-testid={`toth-rule-runs-${r.kind}`}>Storico esecuzioni</button>
          {runs[r.id] && (
            <div className="mt-2 max-h-48 overflow-y-auto rounded-xl bg-muted p-3 text-xs">
              {runs[r.id].length === 0 && "Nessuna esecuzione"}
              {runs[r.id].map((x) => <p key={x.id}>{fmtDT(x.at)} · {x.trigger} · solleciti {x.result.reminders} · escalation {x.result.escalations} · rischi {x.result.risks}</p>)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const Actions = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/toth/actions").then((r) => setRows(r.data));
  }, []);
  if (!rows.length) return <Empty text="Nessuna azione registrata." />;
  return (
    <div className="card-soft overflow-x-auto">
      <table className="w-full min-w-[820px] text-sm" data-testid="toth-actions-table">
        <thead className="bg-muted/60 text-left text-xs uppercase text-muted-foreground"><tr><th className="px-4 py-3">Data</th><th>Azione</th><th>Livello</th><th>Regola</th><th>Richiesta</th><th>Esito</th></tr></thead>
        <tbody>{rows.map((a) => (
          <tr key={a.id} className="border-t align-top">
            <td className="whitespace-nowrap px-4 py-2">{fmtDT(a.at)}</td><td className="py-2 font-semibold">{a.proposed_action}</td>
            <td className="py-2">L{a.level}</td><td className="py-2">{a.rule_name || "—"}</td>
            <td className="max-w-[300px] py-2 text-muted-foreground">{a.request}</td>
            <td className="py-2"><span className={cn("rounded-full px-2 py-0.5 text-xs font-bold", ACTION_TONE[a.status])}>{a.status}</span><p className="text-xs text-muted-foreground">{a.outcome}</p></td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
};

const Powers = ({ s }) => (
  <div className="grid gap-4 lg:grid-cols-2">
    {[1, 2, 3].map((lv) => (
      <div key={lv} className="card-soft p-5" data-testid={`toth-level-${lv}`}>
        <p className="text-xs font-bold uppercase tracking-wider text-primary">Livello {lv}</p><p className="text-lg font-bold">{LEVEL[lv]}</p>
        <ul className="mt-2 list-disc pl-5 text-sm">{Object.values(s.tools).filter((t) => t.level === lv).map((t) => <li key={t.label}>{t.label}</li>)}</ul>
      </div>
    ))}
    <div className="rounded-2xl border-2 border-red-200 bg-red-50 p-5" data-testid="toth-level-4">
      <p className="text-xs font-bold uppercase tracking-wider text-red-700">Livello 4</p><p className="text-lg font-bold">Non delegabile — bloccato nel backend</p>
      <ul className="mt-2 list-disc pl-5 text-sm">{s.forbidden.map((f) => <li key={f}>{f}</li>)}</ul>
    </div>
  </div>
);

const Policies = ({ s, reload }) => {
  const [p, setP] = useState(s.policies);
  const FIELDS = { take_charge_hours: "Presa in carico (h)", first_update_hours: "Primo aggiornamento (h)", reminder_every_hours: "Frequenza solleciti (h)", max_reminders: "Max solleciti", escalate_ceo_after_hours: "Escalation CEO dopo (h)" };
  const save = async () => {
    try {
      await api.put("/toth/settings", { toth_enabled: s.toth_enabled, ai_budget_monthly_usd: s.ai_budget_monthly_usd, policies: p });
      toast.success("Politiche di escalation salvate");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="card-soft overflow-x-auto p-5">
      <table className="w-full min-w-[700px] text-sm" data-testid="toth-policies-table">
        <thead className="text-left text-xs uppercase text-muted-foreground"><tr><th className="py-2">Priorità</th>{Object.values(FIELDS).map((f) => <th key={f}>{f}</th>)}</tr></thead>
        <tbody>{["bassa", "media", "alta", "urgente"].map((pr) => (
          <tr key={pr} className="border-t"><td className="py-2 font-bold">{POLICY_LABEL[pr]}</td>
            {Object.keys(FIELDS).map((k) => <td key={k} className="pr-2"><Input type="number" min="1" value={p[pr][k]} className="h-9 w-24 rounded-lg" data-testid={`policy-${pr}-${k}`}
              onChange={(e) => setP({ ...p, [pr]: { ...p[pr], [k]: Number(e.target.value) } })} /></td>)}</tr>
        ))}</tbody>
      </table>
      <p className="mt-3 text-xs text-muted-foreground">Toth non invia solleciti su ordini con impedimento documentato; per priorità alta/urgente segnala comunque al CEO il rischio potenziale.</p>
      <Button onClick={save} className="mt-4 rounded-xl font-bold" data-testid="toth-policies-save">Salva politiche</Button>
    </div>
  );
};

const TABS = [
  { v: "chat", label: "Chat", roles: null },
  { v: "briefing", label: "Briefing", roles: ["ceo", "amministrazione"] },
  { v: "proposals", label: "Proposte", roles: ["ceo", "amministrazione"] },
  { v: "overview", label: "Controllo", roles: ["ceo"] },
  { v: "rules", label: "Le regole di Toth", roles: ["ceo", "amministrazione"] },
  { v: "takeovers", label: "Prese in carico", roles: ["ceo"] },
  { v: "actions", label: "Storico azioni", roles: ["ceo", "amministrazione"] },
  { v: "powers", label: "Matrice dei poteri", roles: ["ceo"] },
  { v: "policies", label: "Escalation", roles: ["ceo"] },
];

export default function Toth() {
  const { user } = useAuth();
  const isCeo = user.role === "ceo";
  const [sp, setSp] = useSearchParams();
  const [s, setS] = useState(null);
  const load = useCallback(() => {
    if (isCeo) api.get("/toth/settings").then((r) => setS(r.data));
  }, [isCeo]);
  useEffect(() => {
    load();
  }, [load]);
  const tabs = TABS.filter((t) => !t.roles || t.roles.includes(user.role));
  const tab = tabs.some((t) => t.v === sp.get("tab")) ? sp.get("tab") : "chat";
  const body = {
    chat: <TothChat />, briefing: <BriefingPanel />, proposals: <ProposalsPanel />, rules: <Rules isCeo={isCeo} />, actions: <Actions />, takeovers: <TakeoversPanel />,
    overview: s && <Overview s={s} reload={load} />, powers: s && <Powers s={s} />, policies: s && <Policies s={s} reload={load} />,
  };
  return (
    <div data-testid="toth-page">
      <PageHeader title={<span className="flex items-center gap-2"><Sparkles className="h-7 w-7 text-primary" />Toth</span>} subtitle="Direttore operativo virtuale · chat, briefing, proposte e controllo" />
      <Tabs value={tab} onValueChange={(v) => setSp({ tab: v })}>
        <TabsList className="mb-4 h-auto flex-wrap justify-start rounded-xl">
          {tabs.map((t) => <TabsTrigger key={t.v} value={t.v} data-testid={`toth-tab-${t.v}`}>{t.label}</TabsTrigger>)}
        </TabsList>
        {tabs.map((t) => <TabsContent key={t.v} value={t.v}>{body[t.v]}</TabsContent>)}
      </Tabs>
    </div>
  );
}
